import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import type { PromptTemplateCategory, PromptTemplate, PromptTemplateCaseListing } from '../api';
import {
  getPromptTemplates,
  createPromptTemplate,
  updatePromptTemplate,
  deletePromptTemplate,
  getCategories,
  getPromptTemplateCases,
} from '../api';
import {
  PrompthubNav,
  CategoryFilter,
  PromptGrid,
  TemplateFormModal,
  DeleteConfirmDialog,
} from '../components';
import { selectUser, selectIsAdmin } from '../../../redux/selectors';
import { AI_SETTINGS_SYSTEM_PROMPT_PREFILL_KEY } from '../utils/systemPromptCategory';
import { PageShell, Button as ShellButton } from 'components/AppShell';
import { USE_NEW_SHELL } from 'core/flags';
import { Plus } from 'lucide-react';
import styles from './PrompthubPage.module.scss';

const PAGE_SIZE = 20;

export default function PrompthubPage() {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const isAdmin = useSelector(selectIsAdmin);
  const [selectedCategory, setSelectedCategory] = useState<PromptTemplateCategory | null>(null);
  const [prompts, setPrompts] = useState<PromptTemplate[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PromptTemplate | null>(null);
  const [categories, setCategories] = useState<PromptTemplateCategory[]>([]);
  const [cases, setCases] = useState<PromptTemplateCaseListing[]>([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const loadPromptsInFlight = useRef<string | null>(null);

  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const data = await getCategories();
      setCategories(data);
    } catch {
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const loadCases = useCallback(async () => {
    setCasesLoading(true);
    try {
      const res = await getPromptTemplateCases();
      setCases(res.items ?? []);
    } catch {
      setCases([]);
    } finally {
      setCasesLoading(false);
    }
  }, []);

  const loadPrompts = useCallback(async () => {
    const key = `${selectedCategory?.id ?? 'all'}_${selectedCaseId ?? 'all'}`;
    if (loadPromptsInFlight.current === key) return;
    loadPromptsInFlight.current = key;
    setLoading(true);
    try {
      const res = await getPromptTemplates({
        category_id: selectedCategory?.id,
        case_id: selectedCaseId ?? undefined,
        limit: PAGE_SIZE,
        offset: 0,
        sort_column: 'favorite_count',
        sort_direction: 'DESC',
      });
      if (loadPromptsInFlight.current === key) {
        setPrompts(res.items);
        setTotalCount(res.count);
      }
    } catch {
      if (loadPromptsInFlight.current === key) {
        setPrompts([]);
        setTotalCount(0);
      }
    } finally {
      if (loadPromptsInFlight.current === key) {
        loadPromptsInFlight.current = null;
        setLoading(false);
      }
    }
  }, [selectedCategory?.id, selectedCaseId]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  const handleUsePrompt = useCallback(
    (content: string) => {
      try {
        localStorage.setItem('chat_message_text', content);
        navigate('/chat');
      } catch {
        navigate('/chat');
      }
    },
    [navigate]
  );

  const handleApplyToAiSettings = useCallback(
    (content: string) => {
      try {
        localStorage.setItem(AI_SETTINGS_SYSTEM_PROMPT_PREFILL_KEY, JSON.stringify(content));
      } catch {
        // ignore quota / private mode
      }
      navigate('/ai-settings');
    },
    [navigate]
  );

  const handleTemplateSubmit = useCallback(
    async (form: {
      title: string;
      description?: string;
      content: string;
      category_ids: string[];
    }) => {
      if (editingTemplate) {
        await updatePromptTemplate(editingTemplate.id, form);
      } else {
        await createPromptTemplate(form);
      }
      await loadPrompts();
      setTemplateModalOpen(false);
      setEditingTemplate(null);
    },
    [editingTemplate, loadPrompts]
  );

  const handleDeleteTemplate = useCallback(async () => {
    if (!deleteTarget) return;
    await deletePromptTemplate(deleteTarget.id);
    await loadPrompts();
    setDeleteTarget(null);
  }, [deleteTarget, loadPrompts]);

  const handleEditPrompt = useCallback((prompt: PromptTemplate) => {
    setEditingTemplate(prompt);
    setTemplateModalOpen(true);
  }, []);

  const handleDeletePrompt = useCallback((prompt: PromptTemplate) => {
    setDeleteTarget(prompt);
  }, []);

  const handleAddTemplate = useCallback(() => {
    setEditingTemplate(null);
    setTemplateModalOpen(true);
  }, []);

  const catalog = (
    <section className={styles.catalogSection}>
      <CategoryFilter
        categories={categories}
        categoriesLoading={categoriesLoading}
        selectedCategory={selectedCategory}
        onSelect={setSelectedCategory}
        onCategoriesChange={loadCategories}
        isAdmin={isAdmin}
        cases={cases}
        casesLoading={casesLoading}
        selectedCaseId={selectedCaseId}
        onCaseSelect={setSelectedCaseId}
      />
      {!USE_NEW_SHELL && (
        <div className={styles.toolbar}>
          <button type="button" className={styles.addButton} onClick={handleAddTemplate}>
            <AddIcon sx={{ fontSize: 20 }} />
            Создать промпт
          </button>
        </div>
      )}
      {loading ? (
        <div className={styles.loading}>Загрузка…</div>
      ) : (
        <PromptGrid
          prompts={prompts}
          selectedCategory={selectedCategory}
          onUsePrompt={handleUsePrompt}
          onApplyToAiSettings={handleApplyToAiSettings}
          onEditPrompt={handleEditPrompt}
          onDeletePrompt={handleDeletePrompt}
          currentUserId={user?.id}
        />
      )}
      {totalCount > PAGE_SIZE && !loading && (
        <div className={styles.pagination}>
          Показано {prompts.length} из {totalCount}
        </div>
      )}
    </section>
  );

  const modals = (
    <>
      <TemplateFormModal
        open={templateModalOpen}
        onClose={() => {
          setTemplateModalOpen(false);
          setEditingTemplate(null);
        }}
        onSubmit={handleTemplateSubmit}
        template={editingTemplate}
        categories={categories}
      />
      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteTemplate}
        title="Удалить шаблон?"
        message={deleteTarget ? `Шаблон «${deleteTarget.title}» будет удалён.` : ''}
      />
    </>
  );

  if (USE_NEW_SHELL) {
    return (
      <>
        <PageShell
          title="Хранилище промптов"
          subtitle="Каталог шаблонов и кейсов для быстрого старта в чате"
          toolbar={<PrompthubNav />}
          actions={
            <ShellButton
              variant="primary"
              leftIcon={<Plus size={14} strokeWidth={2} />}
              onClick={handleAddTemplate}
            >
              Создать промпт
            </ShellButton>
          }
        >
          {catalog}
        </PageShell>
        {modals}
      </>
    );
  }

  return (
    <div className={styles.root}>
      <PrompthubNav />
      {catalog}
      {modals}
    </div>
  );
}
